import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Granel Vital — mini carrito.
 *
 * Contrato pixel-safe: este componente NUNCA agrega al carrito. El alta la
 * hace el botón nativo del tema, que es quien dispara los eventos estándar que
 * consumen Meta Pixel y Shopify Analytics. Aquí solo se escucha
 * `shopify:cart:lines-update` para abrir el cajón, y los cambios de cantidad
 * despachan ese mismo evento estándar para que el contador del header y el
 * resto de componentes del tema se enteren.
 */
class GvMiniCart extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {Element | null} */
  #lastFocused = null;
  /** @type {boolean} */
  #loaded = false;
  /** @type {number[]} */
  #productIdsInCart = [];

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.refs = {
      panel: this.querySelector('[data-gv-panel]'),
      items: this.querySelector('[data-gv-items]'),
      empty: this.querySelector('[data-gv-empty]'),
      loading: this.querySelector('[data-gv-loading]'),
      footer: this.querySelector('[data-gv-footer]'),
      count: this.querySelector('[data-gv-count]'),
      subtotal: this.querySelector('[data-gv-subtotal]'),
      savings: this.querySelector('[data-gv-savings]'),
      savingsText: this.querySelector('[data-gv-savings-text]'),
      ship: this.querySelector('[data-gv-ship]'),
      shipText: this.querySelector('[data-gv-ship-text]'),
      shipFill: this.querySelector('[data-gv-ship-fill]'),
      cross: this.querySelector('[data-gv-cross]'),
      crossTrack: this.querySelector('[data-gv-cross-track]'),
    };

    this.threshold = Number(this.dataset.threshold) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'MXN';
    this.crossSell = this.dataset.crossSell === 'true';
    this.cartUrl = this.dataset.cartUrl || Theme.routes.cart_url;

    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('keydown', this.#handleKeydown, { signal });

    // El alta la hace el tema; aquí solo reaccionamos a que ocurrió.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });

    // Cualquier disparador de carrito del tema abre este cajón en su lugar.
    document.addEventListener('click', this.#handleTriggerClick, { signal, capture: true });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /* ------------------------------------------------------------------ */
  /* Apertura y cierre                                                   */
  /* ------------------------------------------------------------------ */

  open() {
    if (this.dataset.open === 'true') return;

    this.#lastFocused = document.activeElement;
    this.hidden = false;
    // Un frame para que la transición de translate tenga estado inicial.
    requestAnimationFrame(() => {
      this.dataset.open = 'true';
    });
    document.documentElement.style.setProperty('overflow', 'hidden');

    this.refs.panel?.querySelector('[data-gv-close]')?.focus({ preventScroll: true });
    this.refresh();
  }

  close() {
    if (this.dataset.open !== 'true') return;

    delete this.dataset.open;
    document.documentElement.style.removeProperty('overflow');

    const done = () => {
      if (this.dataset.open !== 'true') this.hidden = true;
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      done();
    } else {
      window.setTimeout(done, 350);
    }

    if (this.#lastFocused instanceof HTMLElement) this.#lastFocused.focus({ preventScroll: true });
    this.#lastFocused = null;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-gv-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const action = target.closest('[data-gv-action]');
    if (!(action instanceof HTMLElement)) return;

    const row = action.closest('[data-line-key]');
    if (!(row instanceof HTMLElement)) return;

    const key = row.dataset.lineKey ?? '';
    const line = Number(row.dataset.line) || 0;
    const quantity = Number(row.dataset.quantity) || 0;

    switch (action.dataset.gvAction) {
      case 'increase':
        this.#changeLine(row, key, line, quantity + 1);
        break;
      case 'decrease':
        this.#changeLine(row, key, line, Math.max(0, quantity - 1));
        break;
      case 'remove':
        this.#changeLine(row, key, line, 0);
        break;
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (this.dataset.open !== 'true') return;

    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    // Trampa de foco: el cajón es modal, el tabulador no debe escaparse a la
    // página de fondo.
    const focusables = this.refs.panel?.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables?.length) return;

    const list = Array.from(focusables).filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
    if (!list.length) return;

    const first = list[0];
    const last = list[list.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * Convierte los disparadores de carrito del tema en aperturas de este cajón.
   * Se registra en fase de captura para ganarle al handler nativo, pero solo
   * cancela el evento cuando efectivamente reconoce un disparador.
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('gv-mini-cart')) return;

    const trigger = target.closest(
      `a[href="${this.cartUrl}"], a[href="${this.cartUrl}/"], [data-testid="cart-drawer-trigger"], .action__cart`
    );
    if (!trigger) return;

    // Cmd/ctrl/click de rueda: el usuario quiere la página del carrito.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    this.open();
  };

  /* ------------------------------------------------------------------ */
  /* Sincronización con el carrito                                       */
  /* ------------------------------------------------------------------ */

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartUpdate = (event) => {
    // Los cambios originados aquí ya repintan el cajón en su propia promesa.
    if (event.target instanceof Element && event.target.closest('gv-mini-cart')) return;

    const shouldOpen = event.action === 'add';

    const wasOpen = this.dataset.open === 'true';

    event.promise
      ?.then(() => {
        // El cross-sell depende del contenido del carrito: se vuelve a pedir
        // cuando ese contenido cambia.
        this.#loaded = false;

        // `open()` refresca al abrir, pero sale temprano si ya estaba abierto:
        // en ese caso el refresco se pide aparte.
        if (shouldOpen && !wasOpen) this.open();
        else if (shouldOpen || wasOpen) this.refresh();
        else this.#fetchCart().then((cart) => cart && this.#renderTotals(cart));
      })
      .catch(() => {
        /* El tema ya informa el error de la petición. */
      });
  };

  async refresh() {
    const cart = await this.#fetchCart();
    if (cart) this.render(cart);
  }

  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { credentials: 'same-origin' });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLElement} row
   * @param {string} key
   * @param {number} line
   * @param {number} quantity
   */
  #changeLine(row, key, line, quantity) {
    row.dataset.busy = 'true';

    const deferred = CartLinesUpdateEvent.createPromise();

    // Se despacha el evento estándar del tema, no uno propio: así el contador
    // del header, la página de carrito y cualquier app suscrita se actualizan.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: key, quantity }],
        promise: deferred.promise,
      })
    );

    fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line, quantity }),
    })
      .then((response) => response.json())
      .then((cart) => {
        if (cart?.status || cart?.errors) throw new Error(cart.description || 'cart error');

        deferred.resolve({
          cart: null,
          detail: { itemCount: cart.item_count, source: 'gv-mini-cart', didError: false },
        });

        this.render(cart);
      })
      .catch((error) => {
        deferred.reject(error);
        row.dataset.busy = 'false';
        this.refresh();
      });
  }

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  /** @param {any} cart */
  render(cart) {
    const { items, empty, footer, count, loading } = this.refs;
    if (count) count.textContent = String(cart.item_count);
    if (loading) loading.hidden = true;

    if (!cart.item_count) {
      if (items) items.innerHTML = '';
      if (empty) empty.hidden = false;
      if (footer) footer.hidden = true;
      if (this.refs.ship) this.refs.ship.hidden = true;
      if (this.refs.cross) this.refs.cross.hidden = true;
      return;
    }

    if (empty) empty.hidden = true;
    if (footer) footer.hidden = false;

    this.#productIdsInCart = cart.items.map((item) => item.product_id);

    if (items) {
      items.innerHTML = cart.items.map((item, index) => this.#itemMarkup(item, index + 1)).join('');
    }

    this.#renderTotals(cart);
    this.#renderCrossSell(cart);
  }

  /** @param {any} cart */
  #renderTotals(cart) {
    const { subtotal, savings, savingsText, ship, shipText, shipFill, count } = this.refs;

    if (count) count.textContent = String(cart.item_count);
    if (subtotal) subtotal.textContent = this.#money(cart.total_price);

    // Ahorro real: descuentos aplicados por Shopify, no un "precio antes"
    // inventado.
    let discount = cart.total_discount || 0;
    if (!discount) {
      discount = cart.items.reduce(
        (sum, item) => sum + Math.max(0, item.original_line_price - item.final_line_price),
        0
      );
    }

    if (savings && savingsText) {
      savings.hidden = discount <= 0;
      if (discount > 0) savingsText.textContent = `Ahorras ${this.#money(discount)} en este pedido`;
    }

    if (!ship || !shipText || !shipFill || this.threshold <= 0) return;

    ship.hidden = false;
    const remaining = this.threshold - cart.total_price;

    // El envío gratis solo aplica hasta 10 kg (el descuento de Shopify excluye
    // tarifas mayores a $175). Arriba de ese peso el cajón no lo promete.
    if (cart.total_weight > GV_MAX_FREE_SHIPPING_GRAMS) {
      ship.dataset.complete = 'false';
      shipText.innerHTML = 'Tu pedido pasa de 10 kg: <strong>el envío se calcula al pagar</strong> según el peso';
      shipFill.style.width = '100%';
      return;
    }

    if (remaining > 0) {
      ship.dataset.complete = 'false';
      shipText.innerHTML = `Te faltan <strong>${this.#money(remaining)}</strong> para envío gratis`;
      shipFill.style.width = `${Math.min(100, (cart.total_price / this.threshold) * 100)}%`;
    } else {
      ship.dataset.complete = 'true';
      shipText.innerHTML = '<strong>Tu pedido tiene envío gratis</strong>';
      shipFill.style.width = '100%';
    }
  }

  /**
   * @param {any} item
   * @param {number} line - Índice 1-based, que es lo que espera /cart/change.js.
   */
  #itemMarkup(item, line) {
    const image = item.image ? item.image.replace(/(\.\w+)(\?|$)/, '_240x280_crop_center$1$2') : '';
    const hasVariant = item.variant_title && item.variant_title !== 'Default Title';
    const discounted = item.original_line_price > item.final_line_price;

    const properties = Object.entries(item.properties || {})
      .filter(([key, value]) => value && !key.startsWith('_'))
      .map(([key, value]) => `${esc(key)}: ${esc(String(value))}`)
      .join(' · ');

    return `
      <li class="gv-cart__item" data-line-key="${esc(item.key)}" data-line="${line}" data-quantity="${item.quantity}" data-busy="false">
        <div class="gv-cart__item-media">
          ${image ? `<img src="${esc(image)}" alt="${esc(item.product_title)}" width="72" height="84" loading="lazy">` : ''}
        </div>
        <div class="gv-cart__item-info">
          <a class="gv-cart__item-title" href="${esc(item.url)}">${esc(item.product_title)}</a>
          ${hasVariant ? `<span class="gv-cart__item-variant">${esc(item.variant_title)}</span>` : ''}
          ${properties ? `<span class="gv-cart__item-props">${properties}</span>` : ''}
          <div class="gv-cart__item-bottom">
            <div class="gv-cart__qty">
              <button type="button" data-gv-action="decrease" aria-label="Quitar una unidad de ${esc(item.product_title)}">${MINUS}</button>
              <output aria-live="polite">${item.quantity}</output>
              <button type="button" data-gv-action="increase" aria-label="Agregar una unidad de ${esc(item.product_title)}">${PLUS}</button>
            </div>
            <span class="gv-cart__item-price">
              <strong>${this.#money(item.final_line_price)}</strong>
              ${discounted ? `<s>${this.#money(item.original_line_price)}</s>` : ''}
            </span>
          </div>
          <button class="gv-cart__item-remove" type="button" data-gv-action="remove">Eliminar</button>
        </div>
      </li>`;
  }

  /** @param {any} cart */
  #renderCrossSell(cart) {
    const { cross, crossTrack } = this.refs;
    if (!this.crossSell || !cross || !crossTrack || this.#loaded || !cart.items.length) return;

    this.#loaded = true;

    const id = cart.items[0].product_id;

    // «complementary» son los complementos que el comerciante define a mano en
    // Search & Discovery. Esta tienda no los tiene cargados, así que devolvía
    // lista vacía y el bloque no llegaba a aparecer nunca. Se intenta primero
    // —es la recomendación de mejor calidad cuando existe— y si viene vacía se
    // cae en «related», que Shopify genera solo y siempre responde.
    const pedir = (intent) =>
      fetch(`/recommendations/products.json?product_id=${id}&limit=8&intent=${intent}`)
        .then((response) => response.json())
        .then((data) =>
          (data.products || [])
            .filter((product) => product.available && !this.#productIdsInCart.includes(product.id))
            .slice(0, 4)
        );

    pedir('complementary')
      .then((lista) => (lista.length ? lista : pedir('related')))
      .then((products) => {
        if (!products.length) {
          cross.hidden = true;
          return;
        }

        crossTrack.innerHTML = products
          .map((product) => {
            const image = product.featured_image
              ? product.featured_image.replace(/(\.\w+)(\?|$)/, '_240x240_crop_center$1$2')
              : '';
            return `
              <li class="gv-cart__cross-card">
                <a class="gv-cart__cross-link" href="${esc(product.url)}">
                  ${image ? `<img src="${esc(image)}" alt="${esc(product.title)}" width="122" height="122" loading="lazy">` : ''}
                  <span class="gv-cart__cross-info">
                    <span class="gv-cart__cross-name">${esc(product.title)}</span>
                    <span class="gv-cart__cross-price">${this.#money(product.price)}</span>
                  </span>
                </a>
              </li>`;
          })
          .join('');

        cross.hidden = false;
      })
      .catch(() => {
        cross.hidden = true;
      });
  }
}

// Peso máximo (en gramos, como lo da /cart.js) con envío gratis.
const GV_MAX_FREE_SHIPPING_GRAMS = 10000;

const MINUS =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M5.5 12h13"/></svg>';
const PLUS =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M12 5.5v13M5.5 12h13"/></svg>';

/**
 * @param {string} value
 * @returns {string}
 */
function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

if (!customElements.get('gv-mini-cart')) {
  customElements.define('gv-mini-cart', GvMiniCart);
}
